import { useMemo } from "react";

import { JobsIcon } from "../../../assets/icons/interface/interface";
import { Button } from "../../../components/ui/button";
import { useRecommendedJobs, useUserAccount } from "../../../services/create-resume/queries";
import JobListingItem from "../../JobsPage/components/JobListingItem";

/**
 * These are picks, not a job board — /jobs already does search, filters and pagination. Showing the
 * whole scored list here turned the landing page into a worse second copy of it.
 */
const MAX_PICKS = 6;

interface RecommendedJobsProps {
	setShowPreferencesModal: (show: boolean) => void;
	setSelectedJob: (job: unknown) => void;
	setShowJobDetailsModal: (show: boolean) => void;
}

interface EmptyStateProps {
	title: string;
	body: string;
	actionLabel: string;
	onAction: () => void;
}

/** Mirrors the FitCheck dashboard empty state, which is this app's established shape for one. */
function EmptyState({ title, body, actionLabel, onAction }: EmptyStateProps) {
	return (
		<div className="py-12 text-center">
			<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
				<JobsIcon className="w-8 h-8 text-gray-400" />
			</div>
			<h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
			<p className="text-gray-500 mb-6 max-w-sm mx-auto text-sm">{body}</p>
			<Button onClick={onAction}>{actionLabel}</Button>
		</div>
	);
}

function RecommendedJobs({ setShowPreferencesModal, setSelectedJob, setShowJobDetailsModal }: RecommendedJobsProps) {
	const { data: account, isLoading: isLoadingAccount } = useUserAccount();

	const areasOfExpertise = account?.jobPreferences?.areasOfExpertise;
	const { data: recommendedJobs, isLoading: isLoadingRecommendedJobs } = useRecommendedJobs(areasOfExpertise);

	// Never list a job with a missing company — skip it entirely.
	const jobs = useMemo(() => (recommendedJobs ?? []).filter((job) => Boolean(job?.company)), [recommendedJobs]);

	if (isLoadingAccount || isLoadingRecommendedJobs) {
		return (
			<div className="animate-pulse">
				{[...new Array(5)].map((_, index) => (
					<div className="h-24 bg-gray-900 opacity-10 mb-3 rounded-md" key={index} />
				))}
			</div>
		);
	}

	// Picks are scored against areas of expertise, so with none set useRecommendedJobs never fires and
	// this slot would render as a blank panel. Say why, rather than leaving the user to guess.
	if (!areasOfExpertise?.length) {
		return (
			<EmptyState
				title="No job picks yet"
				body="We match job picks to your areas of expertise, and yours are not set yet. Add them and your recommendations will show up here."
				actionLabel="Add your areas of expertise"
				onAction={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// Expertise is set but nothing came back — a real result, not a gap in the profile.
	if (jobs.length === 0) {
		return (
			<EmptyState
				title="No matches right now"
				body="Nothing on the job board matches your preferences at the moment. Widening them — more role types, more locations — usually turns up more."
				actionLabel="Edit preferences"
				onAction={() => setShowPreferencesModal(true)}
			/>
		);
	}

	// The "fill in more preferences" nudge lives in the search-inputs panel above, not over this list.
	return (
		<div className="divide-y divide-gray-200">
			{jobs.slice(0, MAX_PICKS).map((job, index) => (
				<JobListingItem
					key={index}
					job={{
						...job,
						logo: job.company.logo_url,
					}}
					onApply={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					onClick={() => {
						setSelectedJob(job);
						setShowJobDetailsModal(true);
					}}
					showCompany={true}
					titleClassName="text-base"
					showMatchScore
				/>
			))}
		</div>
	);
}

export default RecommendedJobs;
